import { useTranslation } from 'react-i18next'

import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/Text'
import { DatasetKindChip } from '@/components/library/DatasetKindChip'
import { DatasetSimulateAudience } from '@/components/library/DatasetSimulateAudience'

import {
  DatasetReadinessReason,
  DatasetReadinessSource,
  useDatasetReadiness,
} from './useDatasetReadiness'

const REASON_KEYS = {
  labels_missing: 'library.preview.dataset.reasonLabelsMissing',
  question_wording_missing: 'library.preview.dataset.reasonWordingMissing',
  ambiguous_columns: 'library.preview.dataset.reasonColumnsUnclear',
  structure_unclear: 'library.preview.dataset.reasonStructureUnclear',
} as const satisfies Record<DatasetReadinessReason, string>

export interface DatasetReadinessCardProps {
  document: DatasetReadinessSource
}

/**
 * Readiness of a Library dataset (B2u, owner Q9): how many questions
 * the upload found, how many of the questions already checked are ready to
 * chart, and for each question judged not ready, what the file lacks. Renders
 * nothing for a non dataset or a status it cannot read; otherwise it brings its
 * own leading separator so the details panel only mounts it.
 */
export function DatasetReadinessCard(props: DatasetReadinessCardProps) {
  const { document } = props
  const { t } = useTranslation('app')

  const readiness = useDatasetReadiness(document)

  if (!readiness) {
    return null
  }

  const { state, questions, ready, notReady, notReadyQuestions } = readiness
  const checked = ready + notReady

  let summary = (
    <div className="space-y-0.5">
      <Text size="sm" data-testid="dataset-readiness-count">
        {t('library.preview.dataset.questionsFound', { count: questions })}
      </Text>
      {checked > 0 ? (
        <Text size="xs" color="muted" data-testid="dataset-readiness-ready">
          {t('library.preview.dataset.readyOfChecked', { ready, checked })}
        </Text>
      ) : null}
    </div>
  )
  if (state === 'listing') {
    summary = (
      <Text size="sm" color="muted" data-testid="dataset-readiness-processing">
        {t('library.preview.dataset.processing')}
      </Text>
    )
  } else if (state === 'failed') {
    summary = (
      <Text size="sm" color="muted" data-testid="dataset-readiness-failed">
        {t('library.preview.dataset.failed')}
      </Text>
    )
  }

  const notReadyList =
    state !== 'listing' && notReadyQuestions.length > 0 ? (
      <div className="space-y-1" data-testid="dataset-readiness-not-ready">
        <Text size="xs" color="muted">
          {t('library.preview.dataset.notReady')}
        </Text>
        <ul className="space-y-1.5">
          {notReadyQuestions.map((question, index) => (
            <li
              key={question.questionId}
              className="text-sm"
              data-testid={`dataset-readiness-question-${index}`}
            >
              <span className="font-medium">{question.label}</span>
              {question.reasons.length > 0 ? (
                <span
                  className="block text-xs text-muted-foreground"
                  data-testid={`dataset-readiness-question-${index}-reasons`}
                >
                  {question.reasons.map((reason) => t(REASON_KEYS[reason])).join(', ')}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    ) : null

  return (
    <>
      <Separator />
      <section className="space-y-2" data-testid="dataset-readiness-card">
        <Text size="sm" className="font-medium">
          {t('library.preview.dataset.title')}
        </Text>
        {state === 'listed' ? <DatasetKindChip kind={readiness.kind} /> : null}
        <DatasetSimulateAudience documentToken={document.token} dataset={readiness} />
        {summary}
        {notReadyList}
      </section>
    </>
  )
}
